import { ShieldCheck, User, BarChart3, Cloud, Lock, Share2, CheckSquare, MapPin, Clock, Users, Eye, RefreshCw, Mail, Flag } from 'lucide-react';
import LegalLayout, { LegalSection, LegalSubsection, BulletList } from '../../components/LegalLayout';

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      badge="Your Privacy Matters"
      badgeIcon={ShieldCheck}
      badgeColor="#2A9D8F"
      lastUpdated="April 5, 2026"
      version="1.0"
      introBorderColor="#2A9D8F"
      intro="AfroConnect is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and mobile application."
    >
      <LegalSection number="1" title="Information We Collect" icon={User}>
        <LegalSubsection title="Personal Information">
          <BulletList items={[
            'Full name',
            'Email address',
            'Age (you must be 18+)',
            'Gender and dating preferences',
            'City (within Canada)',
            'Country of origin',
            'Tribe/ethnicity (optional)',
            'Languages spoken',
            'Religion (optional)',
            'Relationship intentions',
            'Bio/about me text',
          ]} />
        </LegalSubsection>

        <LegalSubsection title="Profile Media">
          <BulletList items={[
            'Profile photos (2–6 required)',
            'Selfie verification photo (optional)',
            'Business/event/community images (if submitted)',
          ]} />
        </LegalSubsection>

        <LegalSubsection title="Technical Data">
          <BulletList items={[
            'IP address',
            'Device type and platform',
            'Push notification tokens',
            'App usage data',
            'Login timestamps',
          ]} />
        </LegalSubsection>
      </LegalSection>

      <LegalSection number="2" title="How We Use Your Information" icon={BarChart3}>
        <p>We use your information to:</p>
        <BulletList items={[
          'Create and manage your account',
          'Facilitate matching with compatible users',
          'Enable messaging between matched users',
          'Verify your identity (selfie verification)',
          'Process premium subscription payments',
          'Send important notifications',
          'Improve our services',
          'Ensure platform safety and security',
          'Comply with legal requirements',
        ]} />
      </LegalSection>

      <LegalSection number="3" title="Third-Party Services" icon={Cloud}>
        <p>We use trusted third-party services:</p>
        <BulletList items={[
          <><strong>Stripe</strong> — Payment processing for subscriptions</>,
          <><strong>Azure Face API</strong> — Facial verification technology</>,
          <><strong>Expo Push Service</strong> — Push notifications</>,
          <><strong>Gmail SMTP</strong> — Email delivery</>,
          <><strong>ip-api.com</strong> — IP geolocation (Canada verification)</>,
        ]} />
        <p>Each service has its own privacy policy governing their use of data.</p>
      </LegalSection>

      <LegalSection number="4" title="Data Storage & Security" icon={Lock} iconColor="#2A9D8F">
        <BulletList items={[
          'All passwords are securely hashed using bcrypt',
          'Data is encrypted in transit using HTTPS',
          'We use JWT tokens for authentication',
          'Payment data is processed by Stripe (PCI compliant)',
          'We implement rate limiting and abuse prevention',
          'Regular security audits are conducted',
        ]} />
      </LegalSection>

      <LegalSection number="5" title="Data Sharing" icon={Share2}>
        <p>We do <strong>NOT</strong> sell your personal data. We may share information:</p>
        <BulletList items={[
          'With other users (profile information you choose to share)',
          'With service providers who help us operate the app',
          'When required by law or to protect rights',
          'In anonymized/aggregated form for analytics',
          'During a business transfer (merger/acquisition)',
        ]} />
      </LegalSection>

      <LegalSection number="6" title="Your Rights" icon={CheckSquare} iconColor="#2A9D8F">
        <p>You have the right to:</p>
        <BulletList items={[
          'Access your personal data',
          'Correct inaccurate data',
          'Delete your account and data',
          'Opt out of marketing emails',
          'Control push notification settings',
          'Request data portability',
          'Withdraw consent at any time',
        ]} />
      </LegalSection>

      <LegalSection number="7" title="Geographic Restrictions" icon={MapPin}>
        <p>AfroConnect is exclusively available in Canada. We verify user location through IP address to ensure compliance. Users outside Canada cannot create accounts.</p>
      </LegalSection>

      <LegalSection number="8" title="Data Retention" icon={Clock}>
        <BulletList items={[
          'Active account data: Retained while account is active',
          'Deleted accounts: Personal data removed within 30 days',
          'Safety/fraud logs: May be retained longer for compliance',
          'Chat messages: Deleted when either user deletes their account',
        ]} />
      </LegalSection>

      <LegalSection number="9" title="Children's Privacy" icon={Users}>
        <p>AfroConnect is strictly for users <strong>18 years and older</strong>. We do not knowingly collect data from minors. If you believe a minor has created an account, please report it immediately.</p>
      </LegalSection>

      <LegalSection number="10" title="Cookies & Tracking" icon={Eye}>
        <p>Our website uses essential cookies for authentication and session management only. Our mobile app does not use cookies. We may collect device identifiers and analytics data to improve performance and user experience.</p>
      </LegalSection>

      <LegalSection number="11" title="Policy Updates" icon={RefreshCw}>
        <p>We may update this Privacy Policy periodically. We will notify you of significant changes via email or in-app notification. Continued use after changes constitutes acceptance.</p>
      </LegalSection>

      <LegalSection number="12" title="Contact Us" icon={Mail}>
        <p>For privacy-related inquiries:</p>
        <BulletList items={[
          <><strong>Email:</strong> <a href="mailto:privacy@afroconnect.ca">privacy@afroconnect.ca</a></>,
          <><strong>Data Protection Officer:</strong> <a href="mailto:dpo@afroconnect.ca">dpo@afroconnect.ca</a></>,
          <><strong>Address:</strong> Toronto, Ontario, Canada</>,
        ]} />
        <p>We respond to all requests within 30 days.</p>
      </LegalSection>

      <LegalSection number="13" title="Canadian Privacy Law" icon={Flag}>
        <p>AfroConnect complies with the <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong> and applicable provincial privacy laws in Canada.</p>
      </LegalSection>

      <div className="text-center pt-4 flex items-center justify-center gap-2 text-[#6D6875] text-sm">
        <ShieldCheck className="w-4 h-4 text-[#2A9D8F]" />
        <span>Your data is protected</span>
      </div>
    </LegalLayout>
  );
}
